import React from 'react'
import {CurrentSlotPositionContext} from '../../analytics/current-slot-position-context'
import {AccountNotificationBanner} from './AccountNotificationBanner'
import {AllContent} from './AllContent'
import {AlternatingTextImage} from './AlternatingTextImage'
import {BrandFilter} from './brand-filter/BrandFilter'
import {ButtonGroup} from './ButtonGroup'
import {CalloutMessage} from './CalloutMessage'
import {CategoryTile, CategoryTiles} from './category-tiles/CategoryTiles'
import {ContentAccordion} from './ContentAccordion'
import {ContentBox} from './ContentBox'
import {ContentPage} from './ContentPage'
import {ContentSlot} from './ContentSlot'
import {ContentTabGroup} from './ContentTabGroup'
import {ContentText} from './ContentText'
import {ContentVideo} from './ContentVideo'
import {CountdownTimer} from './CountdownTimer'
import {GridCard} from './GridCard'
import {HeroBanner} from './HeroBanner'
import {HeroBannerCarousel} from './HeroBannerCarousel'
import {HTMLContentBlock} from './HTMLContentBlock'
import {InfoCardList} from './InfoCardList'
import { ManualProductCarousel } from '../manual-product-carousel'
import {NavFlyoutBanner} from './NavFlyoutBanners'
import {NavFlyoutGrid} from './NavFlyoutGrid'
import {OfferCard} from './offer-cards/OfferCard'
import {OfferCards} from './offer-cards/OfferCards'
import {PlaceholderContent} from './PlaceholderContent'
import {ProductCarousel} from './ProductCarousel'
import {ContentLinkGroup} from './ContentLinkGroup'
import {ContentContactCardGroup} from './ContentContactCardGroup'
import {PromoBanner} from './PromoBanner'
import {ReadMore} from './ReadMore'
import {Recipe} from './Recipe'
import {StickyBanner} from './sticky-banner/StickyBanner'
import {ThinBanner} from './thin-banner'
import {PDPBadgeAssetVizualisationWrapper} from './product-badges'
import {Tab} from '../tabs'
import CategoryPage from '../../pages/category'
import Home from '../../pages/home'
import {MarketingForm} from './marketing-form'
import {ProductInformationVizualization} from './ProductInformation'
import {ContentAccordion2ColumnLayout} from './content-accordion-2-column-layout'
import {useContentStore, useCustomerStore, usePreviewDebugStore, useRapidDeliveryStore} from '../../store/hooks/useStore'
import {observer} from 'mobx-react'
import {XRayPreviewWrapper} from '../amplience-preview/x-ray-preview-wrapper'
import {SlotContentItem, SlotItem} from '../../types/cms'
import {getComponentsBasedOnVariation} from './utils'
import { WelcomeBack } from '../welcome-back'
import { ProductMerchandisingCarousel } from './ProductMerchandisingCarousel'
import { KlarnaOSMContainer } from './KlarnaOSMContainer'
import { CompetitionMarketingForm } from './competition-marketing-form/index'
import { CompetitionEntryBanner } from './competitions/CompetitionEntryBanner'
import { DeliveryBanner } from './delivery-banner'
import PromotionList from './PromotionList'
import { QuickLinks } from './quick-links/QuickLinks'

/**
 * @todo - wrap CMS components with `loadable`.
 * */

export interface SlotProps {
  contentId?: string
  data?: SlotContentItem
  deliveryKey?: string
  gridItemType?: string
  slotIndex?: number
  suppressDefaultContent?: boolean
  isVariationEligible?: (contentItems: SlotItem[]) => boolean
}

export const Slot = observer(({deliveryKey, contentId, data, slotIndex, suppressDefaultContent = false, isVariationEligible}: SlotProps) => {
  const cms = useContentStore()
  const {showSlots} = usePreviewDebugStore()
  const {isRapidDeliveryBooked} = useRapidDeliveryStore()
  // Fetch the slotGroupName: either within an array or within the _meta variable
  const slotGroupName = data?.[0]?.slotGroupName || data?._meta?.name
  const _slotIndex = slotIndex || data?.[0]?.slotIndex
  let content = data
  // If we had no data, try fetching based on the delivery Key
  if(typeof content === 'undefined' || !content) {
    const key = deliveryKey && deliveryKey !== 'null' ? deliveryKey : contentId

    // If we got a key, fetch the content of it
    if (typeof key !== 'undefined' && key) {
      content = cms.contentById[key]
    }
  }

  // Fetch the content
  let components
  if (typeof content !== 'undefined' && content) {
    components = useContentToDisplay(
      content,
      suppressDefaultContent && isRapidDeliveryBooked,
      isVariationEligible,
    )
  }

  // If no content returned, exit here
  if (typeof components === 'undefined' || !components) {
    return null
  }

  // Show the slot content
  return showSlots ? (
    <XRayPreviewWrapper title="SLOT" isComponent={false} content={content}>
      <SlotContent content={components} />
    </XRayPreviewWrapper>
  ) : (
    <SlotContent content={components} slotGroupName={slotGroupName} slotIndex={_slotIndex} />
  )
})
export interface SlotContentProps {
  content: SlotItem[] | null | undefined
  slotGroupName?: string
  slotIndex?: number
}
const SlotContent = ({content, slotGroupName, slotIndex}: SlotContentProps) => {

  const {showContent} = usePreviewDebugStore()

  if (!content) return null

  return (
    <>
      {content
        ?.filter((component): component is SlotItem => !!component)
        ?.map((component, idx) => {
          if (Array.isArray(component) && component.length) {
            return <SlotContent content={component} slotIndex={slotIndex} />
          }

          if (!component?._meta?.schema) {
            return null
          }

          const Component = getComponentForSchema(component._meta.schema)

          if (!Component) return null

          const componentIndex = slotIndex !== undefined ? slotIndex : idx + 1

          return showContent ? (
            <CurrentSlotPositionContext.Provider value={componentIndex}>
              <XRayPreviewWrapper title="CONTENT" isComponent={true} content={component}>
                <Component 
                  key={component._meta.schema + idx} 
                  content={component} 
                  slotGroupName={slotGroupName}
                  slotIndex={componentIndex}
                />
              </XRayPreviewWrapper>
            </CurrentSlotPositionContext.Provider>
          ) : (
            <CurrentSlotPositionContext.Provider value={componentIndex}>
              <Component 
                key={component._meta.schema + idx}
                content={component}
                slotGroupName={slotGroupName}
                slotIndex={componentIndex}
              />
            </CurrentSlotPositionContext.Provider>
          )
        })
      }
    </>
  )
}

export const useContentToDisplay = (
  slotContent: SlotContentItem | undefined,
  suppressDefaultContent = false,
  isVariationEligible?: (contentItems: SlotItem[]) => boolean,
) => {
  const {params, selectedPreviewCustomerGroup} = useContentStore()
  const {currentCustomerGroups} = useCustomerStore()
  const {rapidDeliveryCustomerGroup} = useRapidDeliveryStore()

  try {
    if (!slotContent) {
      return null
    }

    const contentToDisplay = getComponentsBasedOnVariation(
      slotContent,
      params,
      currentCustomerGroups,
      {
        selectedPreviewCustomerGroup,
        suppressDefaultContent,
        rapidDeliveryCustomerGroup,
        isVariationEligible,
      },
    )

    if (!contentToDisplay) return null

    // Makes sure that default content can be returned as a collection.
    return Array.isArray(contentToDisplay) ? contentToDisplay : [contentToDisplay]

  } catch (error) {
    console.error(`Issue rendering slot content. Error: ${error}`)
  }
}

const schemaComponentMap: Record<string, any> = {
  'https://www.iceland.co.uk/content/offer-card.json': OfferCard,
  'https://www.iceland.co.uk/content/offer-cards.json': OfferCards,
  'https://www.iceland.co.uk/content/category-tile.json': CategoryTile,
  'https://www.iceland.co.uk/content/category-tiles.json': CategoryTiles,
  'https://www.iceland.co.uk/content/seo-read-more-text.json': ReadMore,
  'https://www.iceland.co.uk/content/thin-banner.json': ThinBanner,
  'https://www.iceland.co.uk/content/brand-filter.json': BrandFilter,
  'https://www.iceland.co.uk/content/nav-flyout-grid.json': NavFlyoutGrid,
  'https://www.iceland.co.uk/content/nav-flyout-banner.json': NavFlyoutBanner,
  'https://www.iceland.co.uk/content/sticky-banner.json': StickyBanner,
  'https://www.iceland.co.uk/content/sticky-countdown-banner.json': StickyBanner,
  'https://www.iceland.co.uk/content/sticky-prizes-won-banner.json': StickyBanner,
  'https://www.iceland.co.uk/slot/sticky-banner-slot.json': StickyBanner,
  'https://www.iceland.co.uk/slot/all-content-slot.json': AllContent,
  'https://www.iceland.co.uk/content/info-cards.json': InfoCardList,
  'https://www.iceland.co.uk/content/hero-banner.json': HeroBanner,
  'https://www.iceland.co.uk/content/content-text.json': ContentText,
  'https://www.iceland.co.uk/content/carousel.json': HeroBannerCarousel,
  'https://www.iceland.co.uk/content/promo-banner.json': PromoBanner,
  'https://www.iceland.co.uk/content/countdown-timer.json': CountdownTimer,
  'https://www.iceland.co.uk/button-group.json': ButtonGroup,
  'https://www.iceland.co.uk/content/content-callout-message.json': CalloutMessage,
  'https://iceland.co.uk/content/content-box.json': ContentBox,
  'https://www.iceland.co.uk/content/text-image.json': AlternatingTextImage,
  'https://www.iceland.co.uk/content/content-accordion.json': ContentAccordion,
  'https://www.iceland.co.uk/content/product-carousel.json': ProductCarousel,
  'https://www.iceland.co.uk/content/favourites-carousel.json': WelcomeBack,
  'https://www.iceland.co.uk/content/product-category-carousel.json': ProductMerchandisingCarousel,
  'https://www.iceland.co.uk/page/content-page.json': ContentPage,
  'https://www.iceland.co.uk/content/in-grid-card.json': GridCard,
  'https://www.iceland.co.uk/content/content/content-html-block.json': HTMLContentBlock,
  'https://www.iceland.co.uk/content/content-tab-group.json': ContentTabGroup,
  'https://www.iceland.co.uk/content/placeholder-content.json': PlaceholderContent,
  'https://www.iceland.co.uk/content/recipe.json': Recipe,
  'https://www.iceland.co.uk/content/content-video.json': ContentVideo,
  'https://www.iceland.co.uk/content/content-accordion-2-column-layout.json': ContentAccordion2ColumnLayout,
  'https://www.iceland.co.uk/content/image-product-badge.json': PDPBadgeAssetVizualisationWrapper,
  'https://www.iceland.co.uk/content/text-product-badge.json': PDPBadgeAssetVizualisationWrapper,
  'https://www.iceland.co.uk/content/top-right-product-badge.json': PDPBadgeAssetVizualisationWrapper,
  'https://www.iceland.co.uk/content/content-tab.json': Tab,
  'https://www.iceland.co.uk/page/category-page.json': CategoryPage,
  'https://www.iceland.co.uk/page/homepage.json': Home,
  'https://www.iceland.co.uk/content/home-hero-slot-variation.json': AllContent,
  'https://www.iceland.co.uk/slot/content-slot.json': ContentSlot,
  'https://www.iceland.co.uk/slot/landing-body.json': ContentSlot,
  'https://www.iceland.co.uk/slot/content-hero-slot.json': AllContent,
  'https://www.iceland.co.uk/content/marketing-form.json': MarketingForm,
  'https://www.iceland.co.uk/content/competition-entry-banner.json': CompetitionEntryBanner,
  'https://www.iceland.co.uk/content/competition-marketing-form.json' : CompetitionMarketingForm,
  'https://www.iceland.co.uk/content/product-information.json': ProductInformationVizualization,
  'https://www.iceland.co.uk/content/account-notification.json': AccountNotificationBanner,
  'https://www.iceland.co.uk/content/book-delivery-information-slot.json': AllContent,
  'https://www.iceland.co.uk/content/manual-product-carousel.json': ManualProductCarousel,
  'https://www.iceland.co.uk/content/klarna-osm.json' : KlarnaOSMContainer,
  'https://www.iceland.co.uk/content/delivery-banner.json': DeliveryBanner,
  'https://www.iceland.co.uk/content/promotion-list.json': PromotionList,
  'https://www.iceland.co.uk/content/quick-links.json': QuickLinks,
  'https://www.iceland.co.uk/content/content-link-group.json': ContentLinkGroup,
  'https://www.iceland.co.uk/content/content-contact-card-group.json': ContentContactCardGroup,
}

function getComponentForSchema(schema: string) {
  return schemaComponentMap[schema]
}
